import { union, uniq } from 'lodash';
import Controller from '../../shared/logic/Controller';
import Ticket from '../../shared/model/ticket/Ticket';
import { findByProp } from '../../shared/util/collection';
import { epoch, utc } from '../../shared/util/datetime';
import Accounts from './Accounts';
import Filters from './Filters';
import Labels from './Labels';
import Workflows from './Workflows';

export const className = 'Tickets';
export const dependenciesRunInState = 'boardList';
export const fields = {
  list: [
    'id',
    'idOwner',
    'idWorkflow',
    'idStage',
    'idLabels',
    'name',
    {
      values: ['onceOff', 'onceOffDueDate', 'financeDueDate']
    },
    'isDueAhead',
    'isDue',
    'isOverdue',
    'isInProgress',
    'isReadyForVerification',
    'isVerified',
    'isFrozen',
    'isRotten',
    'isSubmitted',
    'isLodged',
    'isConditionallyApproved',
    'isApproved',
    'isSettled',
    'isWon',
    'isWithDrawn',
    'isDeclined',
    'isLost',
    'isActive',
    'orderByField'
  ],
  names: ['id', 'name']
};

export default class Tickets extends Controller {
  ticketsListHeaderSort = (order) => {
    const { orderBy, coerceTo, isDescending } = order;
    const query = { orderBy, coerceTo, isDescending: !isDescending };
    this.Storage.set('OrderBy', query);
    this.reset('available', { query });
  };

  ticketsListHeaderLabel = () => {
    this.Scope.Event.dialogLabelSelect({
      event: null,
      labels: this.Scope.get('Data.Labels.Collection.Data'),
      data: this.getDataCollectionSelection()
    });
  };

  ticketsListHeaderStatus = () => {
    this.Scope.Event.dialogStatusSelect({
      event: null,
      statuses: [
        { id: 'isInProgress', name: 'Open' },
        { id: 'isWon', name: 'Won' },
        { id: 'isLost', name: 'Lost' },
        { id: 'isFrozen', name: 'Paused' }
      ],
      data: this.getDataCollectionSelection()
    });
  };

  ticketsListLabel = (ticket) => {
    this.Scope.Event.dialogLabelSelect({
      event: null,
      labels: this.Scope.get('Data.Labels.Collection.Data'),
      selectedLabels: ticket.idLabels,
      data: ticket
    });
  };

  ticketLabelsSelected = async (selectedLabels, ticket) => {
    if (ticket instanceof Ticket) {
      await this.Data.update({ id: ticket.id, idLabels: selectedLabels });
    }
    if (Array.isArray(ticket)) {
      /* eslint-disable no-restricted-syntax */
      /* eslint-disable no-await-in-loop */
      for (const id of ticket) {
        const t = findByProp(this.getDataCollectionData(), id);
        await this.Data.update({ id, idLabels: uniq(union(t.idLabels, selectedLabels)) });
      }
      /* eslint-enable no-restricted-syntax */
      /* eslint-enable no-await-in-loop */
    }
    this.reset();
  };

  ticketStatusSelected = async (selectedStatus, ticket) => {
    const payload = {
      isActive: true,
      isInProgress: false,
      isWon: false,
      isLost: false,
      isFrozen: false,
      isSettled: false,
      isWithDrawn: false,
      isDeclined: false,
      isSubmitted: false,
      isLodged: false,
      isConditionallyApproved: false,
      isApproved: false,
      wonTimeStamp: '',
      lostTimeStamp: '',
      frozenTimeStamp: '',
      settledTimeStamp: '',
      deletedTimeStamp: '',
      withDrawnTimeStamp: '',
      declinedTimeStamp: '',
      isSubmittedTimeStamp: '',
      isLodgedTimeStamp: '',
      isConditionallyApprovedTimeStamp: '',
      isApprovedTimeStamp: '',
      statusHistoryNote: selectedStatus?.statusNote || '<p>Deal status changed</p>'
    };
    switch (selectedStatus.status.id) {
      case 'isWon':
        payload.isWon = true;
        payload.wonTimeStamp = selectedStatus.statusDate || epoch(utc());
        break;
      case 'isLost':
        payload.isLost = true;
        payload.lostTimeStamp = selectedStatus.statusDate || epoch(utc());
        break;
      case 'isFrozen':
        payload.isFrozen = true;
        payload.frozenTimeStamp = selectedStatus.statusDate || epoch(utc());
        break;
      case 'isInProgress':
      default:
        payload.isInProgress = true;
        break;
    }

    if (ticket instanceof Ticket) {
      await this.Data.update({ id: ticket.id, ...payload });
    }
    if (Array.isArray(ticket)) {
      /* eslint-disable no-restricted-syntax */
      /* eslint-disable no-await-in-loop */
      for (const id of ticket) {
        const t = findByProp(this.getDataCollectionData(), id);
        await this.Data.update({ id, ...payload });
      }
      /* eslint-enable no-restricted-syntax */
      /* eslint-enable no-await-in-loop */
    }
    this.reset();
  };

  ticketsListEdit = (ticket) => {
    this.Scope.State.transitionTo('dealEdit', {
      id: ticket.idWorkflow,
      dd: ticket.id
    });
  };

  ticketsConfirmationModalToggle = () => {
    this.Scope.update(
      'isVisible.ConfirmationModal',
      !this.Scope.get('isVisible.ConfirmationModal')
    );
  };

  ticketsListConfirm = () => {
    // const response = this.Scope.get('Resources.Confirmation.Response');
  };

  ticketsListCancel = () => {
    // const response = this.Scope.get('Resources.Confirmation.Response');
  };

  ticketsFiltersToggle = () => {
    const isVisible = this.Scope.get('isVisible.Filters');
    if (!isVisible) {
      this.Scope.Event.intercomHide();
    } else {
      this.Scope.Event.intercomShow();
    }
    this.Scope.update('isVisible.Filters', !isVisible);
  };

  ticketAdd = () => {
    this.Scope.State.transitionTo('dealEdit', {
      id: this.Scope.get('Workflow.ID'),
      dd: '0'
    });
  };

  ticketView = (ticket) => {
    if (ticket?.id && ticket?.idWorkflow) {
      this.Scope.State.transitionTo('deal', {
        id: ticket.idWorkflow,
        dd: ticket.id
      });
    }
  };

  ticketsBoard = () => {
    this.Scope.State.transitionTo('board', {
      id: this.Scope.get('Workflow.ID')
    });
  };

  ticketsFiltersRemove = () => {
    this.Storage.remove('Filter');
    this.reset('available', { query: { idFilter: '', filterName: '' } });
  };

  ticketsFiltersApply = (id, name) => {
    this.ticketsFiltersToggle();
    const query = { idFilter: id, filterName: name };
    this.Storage.set('Filter', query);
    this.reset('available', { query });
  };

  ticketsStartingLetter = (letter) => {
    const newLetter = letter !== 'ALL' ? letter : '';
    this.Storage.set('StartingWith', newLetter);
    this.reset('available', { query: { startingWith: newLetter } });
  };

  run = async (dependencies, dependenciesForAngular) => {
    const isInRunnableState = await this.init(
      [
        { name: 'ticketsListHeaderSort:click', handler: this.ticketsListHeaderSort },
        {
          name: 'ticketsListHeaderLabel:click',
          handler: this.ticketsListHeaderLabel
        },
        {
          name: 'ticketsListHeaderStatus:click',
          handler: this.ticketsListHeaderStatus
        },

        { name: 'ticketsListLabel:click', handler: this.ticketsListLabel },
        { name: 'ticketsListEdit:click', handler: this.ticketsListEdit },

        { name: 'ticketsListConfirm:click', handler: this.ticketsListConfirm },
        { name: 'ticketsListCancel:click', handler: this.ticketsListCancel },

        { name: 'ticketsBoard:click', handler: this.ticketsBoard },
        { name: 'ticketsFiltersToggle:click', handler: this.ticketsFiltersToggle },
        { name: 'ticketAddNew:click', handler: this.ticketAdd },

        { name: 'itemListClickItem:click', handler: this.ticketView },
        { name: 'itemListSelectItem', handler: undefined },

        { name: 'removeFilter:click', handler: this.ticketsFiltersRemove },
        { name: 'startingLetter:click', handler: this.ticketsStartingLetter }
      ],
      dependencies,
      dependenciesForAngular,
      dependenciesRunInState
    );
    if (!isInRunnableState) {
      return;
    }

    this.Scope.WebPage.pageTitle('Deals');
    await Promise.all([
      this.Scope.get('Data.Accounts')?.load('available'),
      this.Scope.get('Data.Filters')?.load('available', { query: { type: 'ticket' } }),
      this.Scope.get('Data.Labels')?.load('available', { query: { type: 'ticket' } }),
      this.Scope.get('Data.Workflows')?.load('available')
    ]).then(async () => {
      const workflow = findByProp(
        this.Scope.get('Data.Workflows')?.getCollectionData(),
        this.Scope.get('Workflow.ID')
      );
      this.Scope.WebPage.pageTitle(`Deals: ${workflow?.name}`);

      this.Scope.set(
        {
          isReady: {
            Tickets: true,
            Workflows: true,
            Accounts: true,
            Filters: true,
            Labels: true
          },
          ticketLabelsSelected: this.ticketLabelsSelected,
          ticketStatusSelected: this.ticketStatusSelected,
          ticketListApplyFilter: this.ticketsFiltersApply,
          ticketListToggleFilters: this.ticketsFiltersToggle,
          Workflow: { Name: workflow?.name }
        },
        true
      );
    });
  };

  constructor(
    $rootScope,
    $scope,
    $state,
    $timeout,
    webpage,
    eventService,
    authenticationService,
    $stateParams
  ) {
    'ngInject';

    const angularDependencies = [
      $rootScope,
      $scope,
      $state,
      $timeout,
      webpage,
      eventService,
      authenticationService
    ];
    super(angularDependencies, {
      Storage: {
        Table: className
      },
      Scope: {
        Defaults: {
          Name: className,
          Resources: {
            Confirmation: {
              Width: '50',
              Icon: '',
              Type: '',
              Title: '',
              Text: '',
              Response: {
                name: '',
                value: false
              }
            }
          },
          Workflow: {
            ID: $stateParams?.id,
            Name: '-'
          },
          Selection: { Tickets: [] },
          isVisible: {
            Filters: false,
            ConfirmationModal: false
          }
        }
      },
      Data: {
        Model: Ticket,
        ModelName: className,
        Requests: {
          available: {
            url: '/api/v1/tickets/workflow/',
            query: {
              idWorkflow: $stateParams?.id,
              fields: JSON.stringify(fields.list),
              orderBy: 'name',
              coerceTo: 'string',
              isDescending: false
            }
          },
          specific: {
            url: '/api/v1/tickets/specific/',
            method: 'POST',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            },
            query: {
              idTickets: [],
              fields: JSON.stringify(fields.list),
              orderBy: 'name',
              coerceTo: 'string',
              isDescending: false
            },
            useQueryAsPayload: true
          },
          crud: {
            url: '/api/v1/ticket/',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          }
        }
      }
    });

    this.reset = (request, requestParameters, scope, isUpdated) => {
      this.resetDataAndScope(
        request || 'available',
        requestParameters || {},
        scope || {
          Data: { Tickets: this.Data }
        },
        isUpdated || true
      );
    };

    this.run([Accounts, Filters, Labels, Workflows], angularDependencies);
  }
}
